/**
 * Le chat (GDD §18).
 *
 * Un salon unique, volontairement. Des canaux par zone ou par niveau
 * découperaient en silons déserts une population qui tient dans une pièce.
 *
 * **Il faut avoir sécurisé sa partie pour écrire.** Sans pseudonyme il n'y a
 * personne à tenir pour responsable de ce qui est dit, et un compte anonyme se
 * recrée en une seconde : la modération n'aurait aucune prise. On peut lire
 * sans compte, c'est ce qui donne envie d'en avoir un.
 */
import { esc } from "../fmt.ts"
import { ic } from "../sprites.ts"
import { pseudoLocal } from "../../net/compte.ts"
import type { Message } from "../../net/chat.ts"

export interface EtatChat {
  erreur: string | null
  /** message dont l'action « signaler » est ouverte */
  vise: number | null
  /** panneau flottant de grand écran déplié */
  ouvert: boolean
  /** messages arrivés pendant que le panneau était replié */
  nonLus: number
}

/**
 * Le panneau flottant de grand écran.
 *
 * Sur un ordinateur, obliger à quitter son activité pour lire trois lignes est
 * absurde : le chat se pose sur la droite, replié en une simple étiquette tant
 * qu'on ne s'en sert pas. Replié, il compte ce qu'on a manqué — sans cela on
 * n'aurait aucune raison de le rouvrir.
 *
 * Il partage l'état et les gestes du plein écran : mêmes identifiants de
 * formulaire, même bouton de signalement, même rendu de ligne.
 */
export function panneauChat(messages: Message[], e: EtatChat, newsNonLues = 0): string {
  if (!e.ouvert) {
    // **Le compte de l'étiquette reste celui des messages.** J'y avais ajouté les
    // nouveautés non lues : le nombre se mettait alors à désigner deux choses à
    // la fois, et une partie neuve annonçait « 1 » sans qu'un mot ait été dit.
    // La barre du bas porte déjà sa propre pastille pour les nouveautés.
    return `<button class="chatOnglet" id="ouvrirChat" aria-expanded="false">
      ${ic("menu-chat", 20)}Chat${e.nonLus > 0 ? ` <b>${e.nonLus > 99 ? "99+" : e.nonLus}</b>` : ""}
    </button>`
  }
  // **Le panneau donne accès aux nouveautés et aux retours, sans les afficher.**
  //
  // Sur grand écran c'est lui qu'on utilise, jamais l'onglet plein écran : les
  // deux écrans ajoutés y étaient donc invisibles. Mais un journal de version se
  // lit mal dans une colonne de trois cents pixels — les liens basculent sur
  // l'écran entier, où il y a la place.
  return `<div class="chatPanneau">
    <div class="chatTete">
      <span>${ic("menu-chat", 18)} Chat</span>
      <button class="lienSocial" data-versSocial="news" title="Voir les nouveautés">
        Nouveautés${newsNonLues > 0 ? ` <b>${newsNonLues}</b>` : ""}</button>
      <button class="lienSocial" data-versSocial="retours" title="Signaler un bug ou proposer une idée">Bug ?</button>
      <button class="fermerChat" id="fermerChat" title="Replier">✕</button>
    </div>
    ${corpsChat(messages, e)}
  </div>`
}

const heure = (t: number) =>
  new Date(t).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })

export function vueChat(messages: Message[], e: EtatChat): string {
  return `
  <section class="carte plein chat">
    <h2>Chat <em>${messages.length > 0 ? `${messages.length} message${messages.length > 1 ? "s" : ""}` : ""}</em></h2>
    ${corpsChat(messages, e)}
  </section>`
}

/** Les messages, l'erreur et la saisie : identiques dans les deux présentations. */
function corpsChat(messages: Message[], e: EtatChat, moi = pseudoLocal()): string {
  return `
    <div class="clignes" id="clignes">
      ${messages.length === 0
        ? `<p class="aide">Personne n'a encore rien dit. À toi de commencer.</p>`
        : messages.map((m) => `
          <div class="cligne ${m.pseudo === moi ? "moi" : ""}">
            <span class="qui">${esc(m.pseudo)}</span>
            <span class="quand">${heure(m.le)}</span>
            <span class="quoi">${esc(m.texte)}</span>
            ${m.pseudo === moi ? "" : `<button class="signale" data-signaler="${m.id}"
              title="Signaler ce message">${e.vise === m.id ? "signalé" : "⚑"}</button>`}
          </div>`).join("")}
    </div>

    ${e.erreur ? `<p class="aide erreurChat">${esc(e.erreur)}</p>` : ""}

    ${moi
      ? `<form id="formChat" class="ligneChat" autocomplete="off">
          <input name="texte" type="text" maxlength="300" placeholder="Dis quelque chose…" />
          <button class="primaire" type="submit">Envoyer</button>
        </form>
        <p class="aide">Un message toutes les trois secondes. Tu parles sous le
        pseudonyme <b>${esc(moi)}</b> : c'est lui qu'on verra.</p>`
      : `<p class="aide">Tu peux lire, mais il faut avoir <b>sécurisé sa partie</b>
         pour écrire — sans pseudonyme, personne n'est responsable de ce qui se dit.
         Ça se fait en deux champs depuis l'onglet Perso.</p>`}`
}
